import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { useI18n, type Language } from '../i18n';

export default function PublicLayout() {
	const { user, logout } = useAuth();
	const { language, setLanguage, t } = useI18n();
	const location = useLocation();
	const navigate = useNavigate();
	const [mobileNavOpen, setMobileNavOpen] = useState(false);
	const logoSrc = `${import.meta.env.BASE_URL}logo-transparent.png`;

	useEffect(() => {
		setMobileNavOpen(false);
	}, [location.pathname]);

	useEffect(() => {
		if (!mobileNavOpen) {
			return;
		}
		const prev = document.body.style.overflow;
		document.body.style.overflow = 'hidden';
		return () => {
			document.body.style.overflow = prev;
		};
	}, [mobileNavOpen]);

	// Preserve invite code (if present) when navigating to login from public pages
	const inviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('invite') || params.get('code');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();

	// Preserve gardenerInvite code (if present) when navigating to login from public pages
	const gardenerInviteCodeFromUrl = (() => {
		try {
			const params = new URLSearchParams(location.search);
			const raw = params.get('gardenerInvite');
			return raw && raw.trim() ? raw.trim() : null;
		} catch {
			return null;
		}
	})();

	const loginHref = gardenerInviteCodeFromUrl
		? `/login?gardenerInvite=${encodeURIComponent(gardenerInviteCodeFromUrl)}`
		: inviteCodeFromUrl
			? `/login?invite=${encodeURIComponent(inviteCodeFromUrl)}`
			: '/login';

	const seekerEntryFromQuery = (() => {
		try {
			const params = new URLSearchParams(location.search);
			return params.get('entry') === 'seeker';
		} catch {
			return false;
		}
	})();
	const seekerEntryFromSession = (() => {
		try {
			return sessionStorage.getItem('publicEntry') === 'seeker';
		} catch {
			return false;
		}
	})();
	const isSeekerExperience = location.pathname.includes('garden-seeker') || seekerEntryFromQuery || seekerEntryFromSession;
	const seekerRootHref = '/garden-seeker';
	const seekerLoginHref = loginHref.includes('?') ? `${loginHref}&entry=seeker` : `${loginHref}?entry=seeker`;

	useEffect(() => {
		if (!isSeekerExperience) return;
		try {
			sessionStorage.setItem('publicEntry', 'seeker');
		} catch {
			// Ignore storage issues.
		}
	}, [isSeekerExperience]);

	const gardenSyncLook =
		location.pathname.includes('garden-seeker') ||
		location.pathname.includes('try-questionnaire') ||
		(location.pathname.includes('login') && (seekerEntryFromQuery || seekerEntryFromSession));

	useEffect(() => {
		document.body.classList.toggle('garden-sync-body', gardenSyncLook);
		return () => document.body.classList.remove('garden-sync-body');
	}, [gardenSyncLook]);

	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	};

	const handleLogoClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		if (isSeekerExperience) {
			if (location.pathname.includes('garden-seeker')) {
				scrollToTop();
			} else {
				navigate(seekerRootHref);
				setTimeout(scrollToTop, 100);
			}
			return;
		}
		if (location.pathname === '/') {
			scrollToTop();
		} else {
			navigate('/');
			setTimeout(scrollToTop, 100);
		}
	};

	const handleHomeClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		if (location.pathname === '/') {
			scrollToTop();
		} else {
			navigate('/');
			// Scroll to top after navigation
			setTimeout(scrollToTop, 100);
		}
	};

	const handleCalculatorClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		// Always clear saved scroll position when clicking nav link
		sessionStorage.removeItem('calculatorScrollPosition');
		if (location.pathname !== '/calculator') {
			navigate('/calculator');
			// Scroll to top after navigation
			setTimeout(scrollToTop, 100);
		} else {
			// If already on calculator, just scroll to top
			scrollToTop();
		}
	};

	const handleGalleryClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		if (location.pathname !== '/gallery') {
			navigate('/gallery');
			// Scroll to top after navigation
			setTimeout(scrollToTop, 100);
		} else {
			// If already on gallery, just scroll to top
			scrollToTop();
		}
	};

	const handleResourcesClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		if (location.pathname !== '/resources') {
			navigate('/resources');
			setTimeout(scrollToTop, 100);
		} else {
			scrollToTop();
		}
	};

	const handleHowItWorksClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
		e.preventDefault();
		if (location.pathname === '/') {
			// If on homepage, scroll to #process
			const processSection = document.getElementById('process');
			if (processSection) {
				processSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
			}
		} else {
			// If on different page, navigate to homepage and then scroll
			navigate('/');
			setTimeout(() => {
				const processSection = document.getElementById('process');
				if (processSection) {
					processSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
				}
			}, 100);
		}
	};

	const languageOptions: { value: Language; label: string; title: string }[] = [
		{ value: 'en', label: 'EN', title: t('language.english', 'English') },
		{ value: 'de', label: 'DE', title: t('language.german', 'Deutsch') },
		{ value: 'hr', label: 'HR', title: t('language.croatian', 'Hrvatski') }
	];
	const hideLanguageSelector = location.pathname.startsWith('/try-questionnaire');

	return (
		<div className={`public-layout d-flex flex-column min-vh-100${gardenSyncLook ? ' garden-sync-scope' : ''}`}>
			{mobileNavOpen && !isSeekerExperience && (
				<button
					type="button"
					className="public-nav-backdrop"
					aria-label={t('public.closeMenu', 'Close menu')}
					onClick={() => setMobileNavOpen(false)}
				/>
			)}
			<div
				className={`public-nav-drawer d-md-none ${mobileNavOpen && !isSeekerExperience ? 'is-open' : ''}`}
				id="public-nav-drawer"
			>
				<div className="d-flex align-items-center justify-content-between p-3 border-bottom">
					<span className="fw-semibold text-success-emphasis">{t('public.menu', 'Menu')}</span>
					<button
						type="button"
						className="btn-close"
						aria-label={t('public.closeMenu', 'Close menu')}
						onClick={() => setMobileNavOpen(false)}
					/>
				</div>
				<nav className="d-flex flex-column p-3 gap-1">
					{isSeekerExperience ? (
						<>
							<Link
								to={seekerRootHref}
								onClick={() => setMobileNavOpen(false)}
								className="btn btn-light text-start text-success-emphasis fw-medium"
							>
								{t('public.nav.home', 'Home')}
							</Link>
						</>
					) : (
						<>
							<Link
								to="/"
								onClick={(e) => {
									handleHomeClick(e);
									setMobileNavOpen(false);
								}}
								className="btn btn-light text-start text-success-emphasis fw-medium"
							>
								{t('public.nav.home', 'Home')}
							</Link>
							<a
								href="#process"
								onClick={(e) => {
									handleHowItWorksClick(e);
									setMobileNavOpen(false);
								}}
								className="btn btn-light text-start text-success-emphasis fw-medium"
							>
								{t('public.nav.howItWorks', 'How it works')}
							</a>
							<Link
								to="/calculator"
								onClick={(e) => {
									handleCalculatorClick(e);
									setMobileNavOpen(false);
								}}
								className="btn btn-light text-start text-success-emphasis fw-medium"
							>
								{t('public.nav.calculator', 'Calculator')}
							</Link>
							<Link
								to="/gallery"
								onClick={(e) => {
									handleGalleryClick(e);
									setMobileNavOpen(false);
								}}
								className="btn btn-light text-start text-success-emphasis fw-medium"
							>
								{t('public.nav.gallery', 'Gallery')}
							</Link>
							<Link
								to="/resources"
								onClick={(e) => {
									handleResourcesClick(e);
									setMobileNavOpen(false);
								}}
								className="btn btn-light text-start text-success-emphasis fw-medium"
							>
								{t('public.nav.resources', 'Resources')}
							</Link>
						</>
					)}
				</nav>
			</div>
			<header className="landing-header py-3">
				<div className="container d-flex align-items-center justify-content-between gap-2 gap-md-3">
					<div className="d-flex align-items-center gap-2">
						<button
							type="button"
							className={`btn btn-outline-success d-md-none public-menu-toggle ${isSeekerExperience ? 'd-none' : ''}`}
							aria-label={t('public.openMenu', 'Open menu')}
							aria-expanded={mobileNavOpen}
							aria-controls="public-nav-drawer"
							onClick={() => setMobileNavOpen((o) => !o)}
						>
							<i className="bi bi-list fs-4"></i>
						</button>
						<Link
							to={isSeekerExperience ? seekerRootHref : '/'}
							onClick={handleLogoClick}
							className="landing-brand d-inline-flex align-items-center gap-2 text-decoration-none"
						>
							<img
								src={logoSrc}
								alt="HortEra logo"
								className="gh-logo gh-logo--header"
							/>
							<span className="fw-bold text-success-emphasis">HortEra</span>
						</Link>
					</div>
					<nav className="landing-nav d-none d-md-flex align-items-center gap-4">
						{!isSeekerExperience && (
							<>
								<Link to="/" onClick={handleHomeClick} className="landing-nav-link">
									{t('public.nav.home', 'Home')}
								</Link>
								<a href="#process" onClick={handleHowItWorksClick} className="landing-nav-link">
									{t('public.nav.howItWorks', 'How it works')}
								</a>
								<Link to="/calculator" onClick={handleCalculatorClick} className="landing-nav-link">
									{t('public.nav.calculator', 'Calculator')}
								</Link>
								<Link to="/gallery" onClick={handleGalleryClick} className="landing-nav-link">
									{t('public.nav.gallery', 'Gallery')}
								</Link>
								<Link to="/resources" onClick={handleResourcesClick} className="landing-nav-link">
									{t('public.nav.resources', 'Resources')}
								</Link>
							</>
						)}
					</nav>
					<div className="d-flex align-items-center gap-2 flex-shrink-0">
						{!hideLanguageSelector && (
							<select
								className="form-select form-select-sm public-language-select"
								value={language}
								onChange={(e) => setLanguage(e.target.value as Language)}
								aria-label={t('language.selector', 'Language selector')}
								title={languageOptions.find((option) => option.value === language)?.title}
							>
								{languageOptions.map((option) => (
									<option key={option.value} value={option.value} title={option.title}>
										{option.label}
									</option>
								))}
							</select>
						)}
						{user ? (
							<button
								type="button"
								className="btn btn-outline-success btn-sm public-header-cta"
								onClick={() => {
									logout();
									navigate(isSeekerExperience ? seekerRootHref : '/');
								}}
							>
								{t('public.cta.logout', 'Log out')}
							</button>
						) : (
							<Link to={isSeekerExperience ? seekerLoginHref : loginHref} className="btn btn-outline-success btn-sm public-header-cta">
								{t('public.cta.login', 'Log in')}
							</Link>
						)}
						{user && (
							<Link
								to="/app"
								className={`btn btn-success btn-sm public-header-cta ${isSeekerExperience ? 'd-none d-md-inline-flex' : ''}`}
							>
								{t('public.cta.enterApp', 'Enter app')}
							</Link>
						)}
					</div>
				</div>
			</header>
			<main className="flex-grow-1">
				<Outlet />
			</main>
			<footer className="landing-footer py-4">
				<div className="container d-flex flex-column flex-md-row align-items-center justify-content-between gap-3">
					<div className="small text-muted">© {new Date().getFullYear()} HortEra. {t('public.footer.tagline', 'Garden collaboration playground.')}</div>
					<div className="d-flex gap-3 small">
						{isSeekerExperience ? (
							<>
								<Link to="/try-questionnaire?entry=seeker" className="text-muted text-decoration-none">
									{t('public.footer.questionnaire', 'Questionnaire')}
								</Link>
								<Link to="/privacy" className="text-muted text-decoration-none">
									{t('public.footer.privacy', 'Privacy')}
								</Link>
							</>
						) : (
							<>
								<a href="#process" className="text-muted text-decoration-none">
									{t('public.footer.process', 'Process')}
								</a>
								<a href="#showcase" className="text-muted text-decoration-none">
									{t('public.footer.stories', 'Stories')}
								</a>
								<Link to="/resources" className="text-muted text-decoration-none">
									{t('public.nav.resources', 'Resources')}
								</Link>
								<Link to="/privacy" className="text-muted text-decoration-none">
									{t('public.footer.privacy', 'Privacy')}
								</Link>
								<Link to={loginHref} className="text-muted text-decoration-none">
									{t('public.footer.getStarted', 'Get started')}
								</Link>
							</>
						)}
					</div>
				</div>
			</footer>
		</div>
	);
}


